import { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useStoreConfig } from '@config/hooks/useStoreConfig'

export type VideoDataFrame = {
    src: string
    id: string
    title: string
    url: string
    duration: string
    description: string | undefined
    contentUrl: string
    uploadDate: string | undefined
}

declare global {
    interface Window {
        YT: {
            Player: new (videoEl: HTMLIFrameElement, config: unknown) => void
        }
    }
}

interface VideoData {
    target: {
        getVideoData: () => {
            title: string
            video_id: string
        }
        getVideoUrl: () => string
        getDuration: () => string
    }
}

export type AdditionalData = {
    description: string
    publishedAt: string
}

interface Thumbnail {
    url: string
    width: number
    height: number
}

interface Thumbnails {
    default: Thumbnail
    medium: Thumbnail
    high: Thumbnail
    standard: Thumbnail
    maxres: Thumbnail
}

interface Localized {
    title: string
    description: string
}

interface Snippet {
    publishedAt: string
    channelId: string
    title: string
    description: string
    thumbnails: Thumbnails
    channelTitle: string
    tags: string[]
    categoryId: string
    liveBroadcastContent: string
    localized: Localized
}

interface YouTubeVideo {
    kind: string
    etag: string
    id: string
    snippet: Snippet
}

export function useIframeYoutubeVideoFetching(): VideoDataFrame[] {
    const [videoData, setVideoData] = useState<VideoDataFrame[]>([])
    const { asPath } = useRouter()
    const config = useStoreConfig()
    const apiKey = config?.base?.youtubeData?.api_key

    const getAdditionalData = async (
        videoEls: HTMLIFrameElement[]
    ): Promise<{ [videoId: string]: AdditionalData }> => {
        const videoIds = videoEls.map((videoEl) => {
            const src = videoEl?.src ?? ''
            return src.split('/').pop()?.split('?')[0] ?? ''
        })

        const apiUrl = `https://www.googleapis.com/youtube/v3/videos?key=${apiKey}&part=snippet&id=${videoIds.join(
            ','
        )}`

        try {
            const response = await fetch(apiUrl)
            const data = await response.json()

            const additionalData: { [videoId: string]: AdditionalData } = {}
            data.items.forEach((item: YouTubeVideo) => {
                additionalData[item.id] = {
                    description: item.snippet.description,
                    publishedAt: item.snippet.publishedAt
                }
            })

            return additionalData
        } catch (error) {
            console.error('Error fetching video details:', error)
            return {}
        }
    }

    const convertToISO8601 = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600)
        const minutes = Math.floor((seconds % 3600) / 60)
        const remainingSeconds = seconds % 60
        return `PT${hours}H${minutes}M${remainingSeconds}S`
    }

    useEffect(() => {
        const onYouTubeIframeAPIReady = async () => {
            const videoEls: HTMLIFrameElement[] = Array.from(
                document.querySelectorAll('iframe[src*="youtube.com"]')
            )

            if (videoEls.length > 0 && window.YT) {
                try {
                    const data = await getAdditionalData(videoEls)
                    videoEls.forEach((videoEl: HTMLIFrameElement) => {
                        initializePlayer(videoEl, data)
                    })
                } catch (error) {
                    console.error(
                        'Error fetching additional video data:',
                        error
                    )
                }
            }
        }

        const onPlayerReady = async (
            event: VideoData,
            videoEl: HTMLIFrameElement,
            data: { [videoId: string]: AdditionalData }
        ) => {
            const player = event.target
            const videoId = player.getVideoData().video_id
            const title = player.getVideoData().title
            const url = player.getVideoUrl()
            const duration = player.getDuration()
            try {
                const videoDetails: VideoDataFrame = {
                    src: videoEl.src,
                    id: videoEl.id,
                    title,
                    url,
                    description: data[videoId]?.description,
                    duration: convertToISO8601(parseInt(duration)),
                    contentUrl: `https://i.ytimg.com/vi_webp/${videoId}/maxresdefault.webp`,
                    uploadDate: data[videoId]?.publishedAt
                }
                setVideoData((prevData) => [...prevData, videoDetails])
            } catch (error) {
                console.error('Error:', error)
            }
        }

        const initializePlayer = (
            videoEl: HTMLIFrameElement,
            data: { [videoId: string]: AdditionalData }
        ) => {
            if (!videoEl.src.includes('rel=0&enablejsapi=1')) {
                videoEl.src += '?rel=0&enablejsapi=1'
            }
            videoEl.id = 'VideoObject'

            new window.YT.Player(/** NOSONAR */ videoEl, {
                events: {
                    onReady: (event: VideoData) =>
                        onPlayerReady(event, videoEl, data)
                }
            })
        }

        onYouTubeIframeAPIReady()
    }, [asPath])

    return videoData
}
